import type {Category} from "~/types/category";
import {DEFAULT_IMAGES, prepareLink} from "~/utils/images";


export function fetchCategoryBySlug(slug: string, categories: readonly Category[]): Category | null {
    return categories.find(
        c => {
            return c.slug === slug
        }
    ) || null
}

export function getCategoryByCode(code: string, categories: readonly Category[]): Category | null {
    return categories.find(
        c => {
            return c.code === code
        }
    ) || null
}

/** Uploaded category tile from API when present; otherwise missing-image placeholder. */
export function categoryVisualSrc(category: Pick<Category, 'imageUrl'>): string {
    const url = category.imageUrl?.trim()
    return url ? prepareLink(url) : DEFAULT_IMAGES.missing.imageUrl
}

const CATEGORY_IMAGE_PATH = /\/Public\/CategoryProductImages\/(\d+)(?:\/og\.jpg)?\/?(?:\?.*)?$/
const CATEGORY_OG_PATH = /\/Public\/CategoryProductImages\/\d+\/og\.jpg(?:\?.*)?$/i

/**
 * Absolute JPEG URL for Viber / Facebook category previews.
 * Falls back to the site logo when the category has no tile image.
 */
export function categoryShareImage(
    imageUrl: string | null | undefined,
    siteUrl: string,
    apiBaseUrl: string,
): string {
    const site = siteUrl.replace(/\/$/, '')
    const fallback = `${site}${DEFAULT_IMAGES.logo.imageUrl}`
    const trimmed = imageUrl?.trim()
    if (!trimmed) return fallback

    const api = apiBaseUrl.replace(/\/$/, '')
    const resolved = trimmed.startsWith('http')
        ? trimmed
        : trimmed.replace('/api/w4eshop', api)
    const match = resolved.match(CATEGORY_IMAGE_PATH)
    if (!match?.[1]) return fallback

    const og = CATEGORY_OG_PATH.test(resolved)
        ? resolved
        : resolved.replace(CATEGORY_IMAGE_PATH, `/Public/CategoryProductImages/${match[1]}/og.jpg`)
    if (og.startsWith('http')) return og
    return `${site}${og.startsWith('/') ? og : `/${og}`}`
}

/** True when the category tile image is SVG (needs extra inset in capsule tiles). */
export function isCategoryImageSvg(
    category: Pick<Category, 'imageMimeType'> | null | undefined,
): boolean {
    const mime = category?.imageMimeType?.trim().toLowerCase() ?? ''
    return mime === 'image/svg+xml' || mime.includes('svg')
}
